import { useEffect, useMemo, useState } from "react"
import { opsAdminApi, type OpsLlmHealthFilters } from "@/lib/opsAdminApi"

type RangePreset = "24h" | "7d" | "30d" | "custom"

type PropertyOption = { id: string; name: string }

type ApiErrorFn = (result: Record<string, unknown>, fallback: string) => void

type HealthResponse = Awaited<ReturnType<typeof opsAdminApi.getLlmHealth>>

export function OpsAiHealthCard({
  propertyOptions,
  onApiError,
  onOpenProperty,
}: {
  propertyOptions: PropertyOption[]
  onApiError: ApiErrorFn
  onOpenProperty: (propertyId: string) => void
}) {
  const [preset, setPreset] = useState<RangePreset>("24h")
  const [fromMs, setFromMs] = useState(() => Date.now() - (24 * 60 * 60 * 1000))
  const [toMs, setToMs] = useState(() => Date.now())
  const [propertyId, setPropertyId] = useState("")
  const [category, setCategory] = useState("")
  const [surface, setSurface] = useState("")
  const [provider, setProvider] = useState("")
  const [model, setModel] = useState("")
  const [outcome, setOutcome] = useState("")
  const [promptKey, setPromptKey] = useState("")
  const [promptVersion, setPromptVersion] = useState("")
  const [envName, setEnvName] = useState("")
  const [userRole, setUserRole] = useState("")

  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [data, setData] = useState<HealthResponse | null>(null)
  const [eventsCursor, setEventsCursor] = useState("")

  const [traceOpen, setTraceOpen] = useState(false)
  const [traceLoading, setTraceLoading] = useState(false)
  const [recomputeLoading, setRecomputeLoading] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<Record<string, unknown> | null>(null)
  const [traceData, setTraceData] = useState<{ trace_id?: string; chain_id?: string; attempts?: Array<Record<string, unknown>> } | null>(null)

  useEffect(() => {
    if (preset === "custom") return
    const now = Date.now()
    const hours = preset === "24h" ? 24 : preset === "7d" ? 24 * 7 : 24 * 30
    setToMs(now)
    setFromMs(now - (hours * 60 * 60 * 1000))
  }, [preset])

  const filters: OpsLlmHealthFilters = useMemo(() => ({
    from: fromMs,
    to: toMs,
    propertyId,
    category,
    surface,
    provider,
    model,
    outcome,
    promptKey,
    promptVersion,
    env: envName,
    userRole,
    eventsLimit: 80,
  }), [fromMs, toMs, propertyId, category, surface, provider, model, outcome, promptKey, promptVersion, envName, userRole])

  const recentEvents = useMemo(() => {
    const rows = data?.tables?.recent_events
    return Array.isArray(rows) ? rows : []
  }, [data])

  const hasMore = Boolean(data?.events?.has_more)
  const facets = data?.facets || {
    categories: [],
    surfaces: [],
    providers: [],
    models: [],
    prompt_keys: [],
    prompt_versions: [],
    envs: [],
    user_roles: [],
  }
  const facetValues = (values: unknown[]) => values
    .map((value: unknown) => String(value || "").trim())
    .filter(Boolean)

  const surfaceOptions = useMemo(() => {
    const set = new Set<string>(facetValues(facets.surfaces || []))
    for (const row of recentEvents) {
      const value = String(row.surface || "")
      if (value) set.add(value)
    }
    if (surface) set.add(surface)
    return Array.from(set).sort()
  }, [facets.surfaces, recentEvents, surface])

  const providerOptions = useMemo(() => {
    const set = new Set<string>(facetValues(facets.providers || []))
    for (const row of recentEvents) {
      const value = String(row.provider || "")
      if (value) set.add(value)
    }
    if (provider) set.add(provider)
    return Array.from(set).sort()
  }, [facets.providers, recentEvents, provider])

  const modelOptions = useMemo(() => {
    const set = new Set<string>(facetValues(facets.models || []))
    for (const row of recentEvents) {
      const value = String(row.model || "")
      if (value) set.add(value)
    }
    if (model) set.add(model)
    return Array.from(set).sort()
  }, [facets.models, recentEvents, model])

  const promptKeyOptions = useMemo(() => {
    const set = new Set<string>(facetValues(facets.prompt_keys || []))
    for (const row of recentEvents) {
      const value = String(row.prompt_key || "")
      if (value) set.add(value)
    }
    if (promptKey) set.add(promptKey)
    return Array.from(set).sort()
  }, [facets.prompt_keys, recentEvents, promptKey])

  const promptVersionOptions = useMemo(() => {
    const set = new Set<string>(facetValues(facets.prompt_versions || []))
    for (const row of recentEvents) {
      const value = String(row.prompt_version || "")
      if (value) set.add(value)
    }
    if (promptVersion) set.add(promptVersion)
    return Array.from(set).sort()
  }, [facets.prompt_versions, recentEvents, promptVersion])

  const categoryOptions = useMemo(() => {
    const set = new Set<string>(facetValues(facets.categories || []))
    for (const fallback of ["guest", "host", "ops"]) set.add(fallback)
    if (category) set.add(category)
    return Array.from(set).sort()
  }, [facets.categories, category])

  const envOptions = useMemo(() => {
    const set = new Set<string>(facetValues(facets.envs || []))
    set.add("prod")
    if (envName) set.add(envName)
    return Array.from(set).sort()
  }, [facets.envs, envName])

  const userRoleOptions = useMemo(() => {
    const set = new Set<string>(facetValues(facets.user_roles || []))
    for (const fallback of ["owner", "editor", "viewer", "guest", "ops", "system"]) set.add(fallback)
    if (userRole) set.add(userRole)
    return Array.from(set).sort()
  }, [facets.user_roles, userRole])

  useEffect(() => {
    loadHealth().catch(() => {})
  }, [
    filters.from,
    filters.to,
    filters.propertyId,
    filters.category,
    filters.surface,
    filters.provider,
    filters.model,
    filters.outcome,
    filters.promptKey,
    filters.promptVersion,
    filters.env,
    filters.userRole,
  ])

  async function loadHealth(cursor = "", appendEvents = false) {
    if (!appendEvents) setLoading(true)
    const res = await opsAdminApi.getLlmHealth({ ...filters, cursor })
    if (!res.ok) {
      if (!appendEvents) setData(null)
      onApiError(res as unknown as Record<string, unknown>, "Failed to load AI health.")
      if (appendEvents) setLoadingMore(false)
      else setLoading(false)
      return
    }

    if (!appendEvents) {
      setData(res)
      setEventsCursor(res.events?.next_cursor || "")
      setLoading(false)
      return
    }

    const current = data
    const previousRows = Array.isArray(current?.tables?.recent_events) ? current.tables.recent_events : []
    const incomingRows = Array.isArray(res.tables?.recent_events) ? res.tables.recent_events : []
    const merged: HealthResponse = {
      ...res,
      tables: {
        top_surfaces_by_cost: Array.isArray(res.tables?.top_surfaces_by_cost)
          ? res.tables.top_surfaces_by_cost
          : Array.isArray(current?.tables?.top_surfaces_by_cost)
            ? current.tables.top_surfaces_by_cost
            : [],
        top_surfaces_by_error_rate: Array.isArray(res.tables?.top_surfaces_by_error_rate)
          ? res.tables.top_surfaces_by_error_rate
          : Array.isArray(current?.tables?.top_surfaces_by_error_rate)
            ? current.tables.top_surfaces_by_error_rate
            : [],
        top_models_by_latency: Array.isArray(res.tables?.top_models_by_latency)
          ? res.tables.top_models_by_latency
          : Array.isArray(current?.tables?.top_models_by_latency)
            ? current.tables.top_models_by_latency
            : [],
        error_clusters: Array.isArray(res.tables?.error_clusters)
          ? res.tables.error_clusters
          : Array.isArray(current?.tables?.error_clusters)
            ? current.tables.error_clusters
            : [],
        recent_events: [...previousRows, ...incomingRows],
      },
      events: res.events || current?.events,
    }
    setData(merged)
    setEventsCursor(res.events?.next_cursor || "")
    setLoadingMore(false)
  }

  async function handleLoadMore() {
    if (!eventsCursor || loadingMore) return
    setLoadingMore(true)
    await loadHealth(eventsCursor, true)
  }

  async function handleExport() {
    setExporting(true)
    const res = await opsAdminApi.exportLlmHealthJson(filters)
    if (!res.ok || !Array.isArray(res.rows)) {
      onApiError(res as unknown as Record<string, unknown>, "Failed to export AI health events.")
      setExporting(false)
      return
    }
    const payload = {
      exported_at: res.exported_at || new Date().toISOString(),
      count: res.count || res.rows.length,
      filters,
      rows: res.rows,
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
    const href = URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = href
    anchor.download = `ai-health-export-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(href)
    setExporting(false)
  }

  async function handleOpenTrace(event: Record<string, unknown>) {
    const eventId = String(event.id || "")
    const traceId = String(event.trace_id || "")
    if (!eventId && !traceId) return
    setSelectedEvent(event)
    setTraceData(null)
    setTraceOpen(true)
    setTraceLoading(true)
    const res = traceId
      ? await opsAdminApi.getLlmHealthTrace({ traceId })
      : await opsAdminApi.getLlmHealthTrace({ eventId })
    if (!res.ok) {
      onApiError(res as unknown as Record<string, unknown>, "Failed to load trace.")
      setTraceLoading(false)
      return
    }
    setTraceData({ trace_id: res.trace_id, chain_id: res.chain_id, attempts: (res.attempts || []) as Array<Record<string, unknown>> })
    setTraceLoading(false)
  }

  async function handleRecompute() {
    const surfaceValue = String(selectedEvent?.surface || "")
    const propertyValue = String(selectedEvent?.property_id || "")
    if (!surfaceValue || !propertyValue) return
    setRecomputeLoading(true)
    const res = await opsAdminApi.recomputeLlmHealthSurface(surfaceValue, propertyValue)
    if (!res.ok) {
      onApiError(res as unknown as Record<string, unknown>, "Recompute failed.")
    } else {
      await loadHealth()
    }
    setRecomputeLoading(false)
  }

  const kpis = data?.kpis
  const recomputeSurface = String(selectedEvent?.surface || "")
  const recomputeProperty = String(selectedEvent?.property_id || "")
  const recomputeAvailable = Boolean(recomputeSurface && recomputeProperty)

  return (
    <div className="section-card" style={{ padding: 0, overflow: "hidden" }}>
      <div className="ops-table-header">
        <div className="section-card__title" style={{ marginBottom: 0 }}>Ultimate AI Health</div>
        <div className="ops-llm-sync-badge">
          <button type="button" className="sf-button sf-button--sm sf-button--secondary" onClick={() => loadHealth().catch(() => {})}>
            Refresh
          </button>
          <button type="button" className="sf-button sf-button--sm sf-button--secondary" disabled={exporting} onClick={() => handleExport().catch(() => {})}>
            {exporting ? "Exporting…" : "Export"}
          </button>
        </div>
      </div>

      <div className="ops-llm-panel">
        <div className="ops-chip-row ops-llm-presets">
          <FilterChip label="24h" active={preset === "24h"} onClick={() => setPreset("24h")} />
          <FilterChip label="7d" active={preset === "7d"} onClick={() => setPreset("7d")} />
          <FilterChip label="30d" active={preset === "30d"} onClick={() => setPreset("30d")} />
          <FilterChip label="Custom" active={preset === "custom"} onClick={() => setPreset("custom")} />
        </div>

        <div className="ops-llm-filters">
          <select className="audit-filters__select" value={propertyId} onChange={(event) => setPropertyId(event.target.value)}>
            <option value="">All properties</option>
            {propertyOptions.map((item) => <option key={item.id} value={item.id}>{item.name || item.id}</option>)}
          </select>
          <select className="audit-filters__select" value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">All categories</option>
            {categoryOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select className="audit-filters__select" value={surface} onChange={(event) => setSurface(event.target.value)}>
            <option value="">All surfaces</option>
            {surfaceOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select className="audit-filters__select" value={provider} onChange={(event) => setProvider(event.target.value)}>
            <option value="">All providers</option>
            {providerOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select className="audit-filters__select" value={model} onChange={(event) => setModel(event.target.value)}>
            <option value="">All models</option>
            {modelOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select className="audit-filters__select" value={outcome} onChange={(event) => setOutcome(event.target.value)}>
            <option value="">All outcomes</option>
            <option value="ok">ok</option>
            <option value="error">error</option>
            <option value="rate_limited">rate_limited</option>
            <option value="overloaded">overloaded</option>
            <option value="timeout">timeout</option>
            <option value="cache_hit">cache_hit</option>
            <option value="parse_error">parse_error</option>
            <option value="schema_failed">schema_failed</option>
            <option value="partial">partial</option>
            <option value="skipped">skipped</option>
          </select>
          <select className="audit-filters__select" value={envName} onChange={(event) => setEnvName(event.target.value)}>
            <option value="">All envs</option>
            {envOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select className="audit-filters__select" value={userRole} onChange={(event) => setUserRole(event.target.value)}>
            <option value="">All roles</option>
            {userRoleOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>

        <div className="ops-llm-filters">
          <select className="audit-filters__select" value={promptKey} onChange={(event) => setPromptKey(event.target.value)}>
            <option value="">All prompt keys</option>
            {promptKeyOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select className="audit-filters__select" value={promptVersion} onChange={(event) => setPromptVersion(event.target.value)}>
            <option value="">All prompt versions</option>
            {promptVersionOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>

        {preset === "custom" && (
          <div className="ops-llm-filters">
            <input className="audit-filters__input" type="datetime-local" value={toDatetimeLocal(fromMs)} onChange={(event) => setFromMs(parseDatetimeLocal(event.target.value, fromMs))} />
            <input className="audit-filters__input" type="datetime-local" value={toDatetimeLocal(toMs)} onChange={(event) => setToMs(parseDatetimeLocal(event.target.value, toMs))} />
          </div>
        )}

        {loading ? (
          <div style={{ padding: "var(--sf-space-8)", textAlign: "center" }}>
            <div className="sf-spinner" style={{ margin: "0 auto" }} />
          </div>
        ) : (
          <>
            <div className="ops-llm-kpi-grid">
              <KpiCard title="Calls" value={formatInt(kpis?.calls)} sublabel="total" />
              <KpiCard title="Success %" value={`${Number(kpis?.success_pct || 0).toFixed(2)}%`} sublabel="ok" />
              <KpiCard title="Error %" value={`${Number(kpis?.error_pct || 0).toFixed(2)}%`} sublabel="error" />
              <KpiCard title="p95 Latency" value={`${formatInt(kpis?.p95_latency_ms)} ms`} sublabel="overall" />
              <KpiCard title="Tokens" value={formatInt(kpis?.tokens)} sublabel="in+out" />
              <KpiCard title="Cost" value={formatUsd(kpis?.cost_total_usd)} sublabel="estimated" />
              <KpiCard title="Cache Hit" value={`${((kpis?.cache_hit_rate || 0) * 100).toFixed(1)}%`} sublabel="event" />
            </div>

            <div className="ops-ai-health-trends">
              <MiniTrend title="Calls" points={data?.trends?.calls || []} />
              <MiniTrend title="Errors" points={data?.trends?.errors || []} />
              <MiniTrend title="Latency" points={data?.trends?.latency || []} suffix=" ms" />
              <MiniTrend title="Cost" points={data?.trends?.cost || []} prefix="$" fixed={4} />
              <MiniTrend title="Tokens" points={data?.trends?.tokens || []} />
            </div>

            <div className="ops-bottom-grid--three">
              <SimpleTable
                title="Top Surfaces by Cost"
                headers={["Surface", "Calls", "Cost"]}
                rows={(data?.tables?.top_surfaces_by_cost || []).map((row: any) => [row.surface, formatInt(row.calls), formatUsd(row.cost_total_usd)])}
                emptyLabel="No cost data."
              />
              <SimpleTable
                title="Top Surfaces by Error Rate"
                headers={["Surface", "Errors", "Error %"]}
                rows={(data?.tables?.top_surfaces_by_error_rate || []).map((row: any) => [row.surface, `${formatInt(row.errors)} / ${formatInt(row.calls)}`, `${Number(row.error_rate_pct || 0).toFixed(2)}%`])}
                emptyLabel="No error clusters in range."
              />
              <SimpleTable
                title="Top Models by Latency"
                headers={["Model", "Calls", "Avg Latency"]}
                rows={(data?.tables?.top_models_by_latency || []).map((row: any) => [`${row.provider}/${row.model}`, formatInt(row.calls), `${formatInt(row.avg_latency_ms)} ms`])}
                emptyLabel="No model latency data."
              />
            </div>

            <div className="data-table-wrapper">
              <div className="ops-llm-table-title">Error Clusters</div>
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th>Cluster</th>
                    <th>Surface</th>
                    <th>Taxonomy</th>
                    <th>Count</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.tables?.error_clusters || []).map((row: any) => (
                    <tr key={row.cluster_key}>
                      <td className="ops-llm-mono">{row.cluster_key}</td>
                      <td>{row.surface}</td>
                      <td>{row.error_taxonomy || row.error_type || "—"}</td>
                      <td>{formatInt(row.count_total)}</td>
                      <td>{formatTime(row.last_seen_ts)}</td>
                    </tr>
                  ))}
                  {(data?.tables?.error_clusters || []).length === 0 && (
                    <tr><td colSpan={5} className="ops-llm-empty">No error clusters found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="data-table-wrapper">
              <div className="ops-llm-table-title">Recent Events Stream</div>
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Surface</th>
                    <th>Model</th>
                    <th>Outcome</th>
                    <th>Latency</th>
                    <th>Cost</th>
                    <th>Property</th>
                    <th>Trace</th>
                  </tr>
                </thead>
                <tbody>
                  {recentEvents.map((event) => (
                    <tr key={String(event.id)}>
                      <td className="ops-llm-mono">{formatTime(Number(event.ts || 0))}</td>
                      <td>{String(event.surface || "—")}</td>
                      <td>{`${String(event.provider || "")}/${String(event.model || "")}`}</td>
                      <td>
                        <span className={`status-pill status-pill--${String(event.status || "") === "ok" ? "healthy" : String(event.status || "") === "error" ? "error" : "warning"}`}>
                          <span className="status-pill__dot" />
                          {String(event.outcome || event.status || "unknown")}
                        </span>
                      </td>
                      <td>{formatInt(Number(event.latency_total_ms || 0))} ms</td>
                      <td>{formatUsd(Number(event.estimated_cost_usd || 0))}</td>
                      <td>
                        {String(event.property_id || "") ? (
                          <button type="button" className="data-table__action" onClick={() => onOpenProperty(String(event.property_id || ""))}>
                            {String(event.property_name || event.property_id || "")}
                          </button>
                        ) : "system"}
                      </td>
                      <td>
                        <button type="button" className="data-table__action" onClick={() => handleOpenTrace(event as Record<string, unknown>).catch(() => {})}>
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                  {recentEvents.length === 0 && (
                    <tr><td colSpan={8} className="ops-llm-empty">No events found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div className="ops-llm-load-more">
                <button type="button" className="data-table__action" disabled={loadingMore} onClick={() => handleLoadMore().catch(() => {})}>
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {traceOpen && (
        <div className="ops-ai-health-modal" role="dialog" aria-modal="true">
          <div className="ops-ai-health-modal__backdrop" onClick={() => setTraceOpen(false)} role="presentation" aria-hidden="true" />
          <div className="ops-ai-health-modal__card">
            <div className="ops-table-header">
              <div className="section-card__title" style={{ marginBottom: 0 }}>Trace Viewer</div>
              <div className="ops-llm-sync-badge">
                <button type="button" className="sf-button sf-button--sm sf-button--secondary" onClick={() => setTraceOpen(false)}>Close</button>
              </div>
            </div>
            <div className="ops-llm-panel">
              {traceLoading ? (
                <div style={{ padding: "var(--sf-space-8)", textAlign: "center" }}>
                  <div className="sf-spinner" style={{ margin: "0 auto" }} />
                </div>
              ) : (
                <>
                  <div className="ops-ai-health-trace-meta">
                    <span className="ops-llm-mono">trace: {traceData?.trace_id || "—"}</span>
                    <span className="ops-llm-mono">chain: {traceData?.chain_id || "—"}</span>
                    <button
                      type="button"
                      className="sf-button sf-button--sm sf-button--secondary"
                      onClick={() => handleRecompute().catch(() => {})}
                      disabled={recomputeLoading || !recomputeAvailable}
                      title={recomputeAvailable ? `Recompute ${recomputeSurface} for ${recomputeProperty}` : "Recompute unavailable for this trace"}
                    >
                      {recomputeLoading ? "Running…" : "Recompute"}
                    </button>
                  </div>
                  <div className="data-table-wrapper">
                    <table className="data-table data-table--compact">
                      <thead>
                        <tr>
                          <th>Attempt</th>
                          <th>Provider/Model</th>
                          <th>Status</th>
                          <th>Latency</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(traceData?.attempts || []).map((row, index) => (
                          <tr key={String(row.id || `${index}`)}>
                            <td className="ops-llm-mono">{Number(row.attempt_index || 0)}</td>
                            <td>{`${String(row.provider || "")}/${String(row.model || "")}`}</td>
                            <td>{String(row.outcome || row.status || "")}</td>
                            <td>{formatInt(Number(row.latency_total_ms || 0))} ms</td>
                            <td>{String(row.error_message || "—")}</td>
                          </tr>
                        ))}
                        {(traceData?.attempts || []).length === 0 && (
                          <tr><td colSpan={5} className="ops-llm-empty">No attempts.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {(traceData?.attempts || []).map((row, index) => (
                    <details key={`payload-${String(row.id || index)}`} className="ops-ai-health-trace-payload">
                      <summary>Attempt {Number(row.attempt_index || 0)} payload context</summary>
                      <div className="ops-ai-health-trace-payload__block">
                        <div className="ops-llm-table-title">Cache/Freshness</div>
                        <pre>{JSON.stringify({
                          cache_mode: row.cache_mode,
                          cache_hit: row.cache_hit,
                          freshness_state: row.freshness_state,
                          stale_reason: row.stale_reason,
                          schema_validation_result: row.schema_validation_result,
                          parse_ok: row.parse_ok,
                        }, null, 2)}</pre>
                      </div>
                      <div className="ops-ai-health-trace-payload__block">
                        <div className="ops-llm-table-title">Request (sanitised)</div>
                        <pre>{String(row.payload_request_json || "") || "(not captured)"}</pre>
                      </div>
                      <div className="ops-ai-health-trace-payload__block">
                        <div className="ops-llm-table-title">Response (sanitised)</div>
                        <pre>{String(row.payload_response_json || "") || "(not captured)"}</pre>
                      </div>
                    </details>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function MiniTrend({ title, points, prefix = "", suffix = "", fixed = 0 }: { title: string; points: Array<{ ts: number; value: number }>; prefix?: string; suffix?: string; fixed?: number }) {
  if (!points.length) {
    return (
      <div className="data-table-wrapper">
        <div className="ops-llm-table-title">{title}</div>
        <div className="ops-llm-empty">No data</div>
      </div>
    )
  }

  const values = points.map((point) => Number(point.value) || 0)
  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const spread = Math.max(1, max - min)
  const width = 260
  const height = 70
  const pad = 8
  const x = (index: number) => pad + ((width - pad * 2) * index) / Math.max(1, points.length - 1)
  const y = (value: number) => height - pad - ((height - pad * 2) * (value - min)) / spread
  const path = points.map((point, index) => `${index === 0 ? "M" : "L"} ${x(index).toFixed(1)} ${y(Number(point.value) || 0).toFixed(1)}`).join(" ")
  const latest = values[values.length - 1] || 0

  return (
    <div className="data-table-wrapper">
      <div className="ops-llm-table-title">{title}</div>
      <svg viewBox={`0 0 ${width} ${height}`} className="ops-ai-health-trend">
        <path d={path} className="ops-ai-health-trend__line" />
      </svg>
      <div className="ops-summary-card__line">{prefix}{fixed > 0 ? latest.toFixed(fixed) : formatInt(latest)}{suffix}</div>
    </div>
  )
}

function SimpleTable({ title, headers, rows, emptyLabel }: { title: string; headers: string[]; rows: string[][]; emptyLabel: string }) {
  return (
    <div className="data-table-wrapper">
      <div className="ops-llm-table-title">{title}</div>
      <table className="data-table data-table--compact">
        <thead>
          <tr>
            {headers.map((header) => <th key={header}>{header}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${title}-${index}`}>
              {row.map((cell, cellIndex) => <td key={`${title}-${index}-${cellIndex}`}>{cell}</td>)}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={headers.length} className="ops-llm-empty">{emptyLabel}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function KpiCard({ title, value, sublabel }: { title: string; value: string; sublabel: string }) {
  return (
    <div className="ops-summary-card">
      <div className="ops-summary-card__title">{title}</div>
      <div className="ops-summary-card__line">{value}</div>
      <div className="ops-summary-card__line ops-summary-card__line--muted">{sublabel}</div>
    </div>
  )
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`ops-filter-chip${active ? " ops-filter-chip--active" : ""}`} onClick={onClick}>
      {label}
    </button>
  )
}

function toDatetimeLocal(ms: number) {
  const date = new Date(ms)
  const pad = (value: number) => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function parseDatetimeLocal(value: string, fallback: number) {
  const ts = Date.parse(value)
  if (!Number.isFinite(ts)) return fallback
  return Math.floor(ts)
}

function formatUsd(value?: number) {
  return `$${Number(value || 0).toFixed(4)}`
}

function formatInt(value?: number) {
  return new Intl.NumberFormat().format(Math.max(0, Math.floor(Number(value) || 0)))
}

function formatTime(ms: number) {
  if (!Number.isFinite(Number(ms))) return "—"
  return new Date(Number(ms)).toLocaleString()
}
